import { create } from 'zustand';
import { TaskStatus, TaskPriority, TaskFilters } from '@/app/types/task';

interface TaskStoreState {
  // Filter state
  filters: TaskFilters;
  setFilters: (filters: Partial<TaskFilters>) => void;
  clearFilters: () => void;

  // Search query
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Quick create modal state
  isCreateModalOpen: boolean;
  openCreateModal: (taskType?: string) => void;
  closeCreateModal: () => void;

  // Pre-filled data for quick create (e.g., when creating task for a specific party)
  prefilledPartyId?: number;
  setPrefilledPartyId: (partyId: number | undefined) => void;

  // Pre-filled task type for quick create (e.g., from Appointments view)
  prefilledTaskType?: string;
  setPrefilledTaskType: (taskType: string | undefined) => void;

  // Selected task for detail view
  selectedTaskId?: number;
  setSelectedTaskId: (taskId: number | undefined) => void;
}

const initialFilters: TaskFilters = {
  status: undefined,
  priority: undefined,
  assigned_to_user_id: undefined,
  parent_entity_type: undefined,
  parent_entity_id: undefined,
  task_category: undefined,
  due_date_from: undefined,
  due_date_to: undefined,
  search: undefined,
};

/**
 * Task Store
 *
 * Global state management for task-related UI state:
 * - Task filters (status, priority, assigned_to, etc.)
 * - Search query
 * - Create modal state
 * - Pre-filled data for quick actions
 * - Selected task for detail view
 */
export const useTaskStore = create<TaskStoreState>((set) => ({
  // Filter state
  filters: initialFilters,
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),
  clearFilters: () => set({ filters: initialFilters }),

  // Search query
  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  // Quick create modal
  isCreateModalOpen: false,
  openCreateModal: (taskType) => set({ isCreateModalOpen: true, prefilledTaskType: taskType }),
  closeCreateModal: () => set({ isCreateModalOpen: false, prefilledPartyId: undefined, prefilledTaskType: undefined }),

  // Prefilled data
  prefilledPartyId: undefined,
  setPrefilledPartyId: (partyId) => set({ prefilledPartyId: partyId }),

  // Prefilled task type
  prefilledTaskType: undefined,
  setPrefilledTaskType: (taskType) => set({ prefilledTaskType: taskType }),

  // Selected task
  selectedTaskId: undefined,
  setSelectedTaskId: (taskId) => set({ selectedTaskId: taskId }),
}));
